import React, { useState } from 'react';
import { Type, Check, Copy, Settings, Eye } from 'lucide-react';

const FONT_FAMILIES = [
  { id: 'inter', name: 'Inter', family: "'Inter', sans-serif", category: 'Sans-Serif', description: 'Default UI font — clean, modern, highly readable' },
  { id: 'roboto', name: 'Roboto', family: "'Roboto', sans-serif", category: 'Sans-Serif', description: 'Google Material Design font — versatile and neutral' },
  { id: 'poppins', name: 'Poppins', family: "'Poppins', sans-serif", category: 'Sans-Serif', description: 'Geometric sans-serif — friendly and modern' },
  { id: 'opensans', name: 'Open Sans', family: "'Open Sans', sans-serif", category: 'Sans-Serif', description: 'Humanist sans-serif — excellent readability' },
  { id: 'lato', name: 'Lato', family: "'Lato', sans-serif", category: 'Sans-Serif', description: 'Semi-rounded — warm and stable' },
  { id: 'nunito', name: 'Nunito', family: "'Nunito', sans-serif", category: 'Sans-Serif', description: 'Rounded terminals — soft and approachable' },
  { id: 'montserrat', name: 'Montserrat', family: "'Montserrat', sans-serif", category: 'Sans-Serif', description: 'Urban typography — bold headlines' },
  { id: 'raleway', name: 'Raleway', family: "'Raleway', sans-serif", category: 'Sans-Serif', description: 'Elegant thin weight — display and body' },
  { id: 'sourcesans', name: 'Source Sans 3', family: "'Source Sans 3', sans-serif", category: 'Sans-Serif', description: 'Adobe open-source — UI and reading' },
  { id: 'dmsans', name: 'DM Sans', family: "'DM Sans', sans-serif", category: 'Sans-Serif', description: 'Low-contrast geometric — clean dashboards' },
  { id: 'worksans', name: 'Work Sans', family: "'Work Sans', sans-serif", category: 'Sans-Serif', description: 'Optimized for screens — compact and clear' },
  { id: 'firasans', name: 'Fira Sans', family: "'Fira Sans', sans-serif", category: 'Sans-Serif', description: 'Mozilla typeface — technical and precise' },
  { id: 'plusjakarta', name: 'Plus Jakarta Sans', family: "'Plus Jakarta Sans', sans-serif", category: 'Sans-Serif', description: 'Modern geometric — trendy SaaS apps' },
  { id: 'spacegrotesk', name: 'Space Grotesk', family: "'Space Grotesk', sans-serif", category: 'Sans-Serif', description: 'Proportional monospace feel — tech dashboards' },
  { id: 'jetbrains', name: 'JetBrains Mono', family: "'JetBrains Mono', monospace", category: 'Monospace', description: 'Developer font — code and data tables' },
  { id: 'firacode', name: 'Fira Code', family: "'Fira Code', monospace", category: 'Monospace', description: 'Ligature monospace — code display' },
  { id: 'sourcecodepro', name: 'Source Code Pro', family: "'Source Code Pro', monospace", category: 'Monospace', description: 'Adobe monospace — logs and terminal' },
  { id: 'ibmplexmono', name: 'IBM Plex Mono', family: "'IBM Plex Mono', monospace", category: 'Monospace', description: 'IBM typeface — structured data' },
  { id: 'merriweather', name: 'Merriweather', family: "'Merriweather', serif", category: 'Serif', description: 'Screen-optimized serif — long-form reading' },
  { id: 'playfair', name: 'Playfair Display', family: "'Playfair Display', serif", category: 'Serif', description: 'High contrast serif — elegant headings' },
];

const FONT_SIZES = [
  { label: '10px', value: 10 },
  { label: '11px', value: 11 },
  { label: '12px', value: 12 },
  { label: '13px', value: 13 },
  { label: '14px (Default)', value: 14 },
  { label: '15px', value: 15 },
  { label: '16px', value: 16 },
  { label: '18px', value: 18 },
  { label: '20px', value: 20 },
  { label: '24px', value: 24 },
  { label: '28px', value: 28 },
  { label: '32px', value: 32 },
  { label: '36px', value: 36 },
  { label: '48px', value: 48 },
];

const FONT_WEIGHTS = [
  { label: 'Light (300)', value: 300 },
  { label: 'Regular (400)', value: 400 },
  { label: 'Medium (500)', value: 500 },
  { label: 'Semi-Bold (600)', value: 600 },
  { label: 'Bold (700)', value: 700 },
];

const SAMPLE_TEXTS = {
  paragraph: 'The quick brown fox jumps over the lazy dog. Pack my box with five dozen liquor jugs. How vexingly quick daft zebras jump!',
  heading: 'Way2Connect SMPP Platform',
  numbers: '0123456789 ₹1,234.56 +91-98765-43210',
  mixed: 'SMS Report: 12,450 sent | 98.2% delivered | TPS: 145.7 | DLR: DELIVRD',
  code: 'SELECT COUNT(*) FROM sms_reports WHERE status = "DELIVERED" AND DATE(created_at) = CURDATE();',
};

const FontSamplePage = () => {
  const [selectedFont, setSelectedFont] = useState(FONT_FAMILIES[0]);
  const [selectedSize, setSelectedSize] = useState(14);
  const [selectedWeight, setSelectedWeight] = useState(400);
  const [sampleText, setSampleText] = useState(SAMPLE_TEXTS.paragraph);
  const [customText, setCustomText] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [copied, setCopied] = useState('');
  const [fontsLoaded, setFontsLoaded] = useState({});

  const loadFont = (font) => {
    if (fontsLoaded[font.id]) return;
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${font.name.replace(/ /g, '+')}:wght@300;400;500;600;700&display=swap`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    setFontsLoaded(prev => ({ ...prev, [font.id]: true }));
  };

  const handleSelectFont = (font) => {
    loadFont(font);
    setSelectedFont(font);
  };

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const filteredFonts = filterCategory === 'all'
    ? FONT_FAMILIES
    : FONT_FAMILIES.filter(f => f.category === filterCategory);

  const displayText = customText || sampleText;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Type className="w-7 h-7 text-indigo-600" />
          Font Sample Library
        </h1>
        <p className="text-sm text-gray-500 mt-1">Browse fonts, adjust sizes, and preview how they look in the platform</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left: Font List */}
        <div className="xl:col-span-1">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Font Families</h2>
              <div className="flex gap-1.5 flex-wrap">
                {['all', 'Sans-Serif', 'Monospace', 'Serif'].map(cat => (
                  <button
                    key={cat}
                    onClick={() => setFilterCategory(cat)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                      filterCategory === cat
                        ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                        : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {cat === 'all' ? 'All' : cat}
                  </button>
                ))}
              </div>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {filteredFonts.map(font => (
                <button
                  key={font.id}
                  onClick={() => handleSelectFont(font)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                    selectedFont.id === font.id ? 'bg-indigo-50 border-l-3 border-l-indigo-500' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900" style={{ fontFamily: fontsLoaded[font.id] ? font.family : 'inherit' }}>
                        {font.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{font.description}</p>
                    </div>
                    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                      font.category === 'Sans-Serif' ? 'bg-blue-50 text-blue-600' :
                      font.category === 'Monospace' ? 'bg-green-50 text-green-600' :
                      'bg-purple-50 text-purple-600'
                    }`}>
                      {font.category}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Preview & Controls */}
        <div className="xl:col-span-2 space-y-6">
          {/* Controls */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-900">Controls</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Font Size</label>
                <select
                  value={selectedSize}
                  onChange={(e) => setSelectedSize(Number(e.target.value))}
                  className="w-full"
                >
                  {FONT_SIZES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Font Weight</label>
                <select
                  value={selectedWeight}
                  onChange={(e) => setSelectedWeight(Number(e.target.value))}
                  className="w-full"
                >
                  {FONT_WEIGHTS.map(w => (
                    <option key={w.value} value={w.value}>{w.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Sample Text</label>
                <select
                  value={sampleText}
                  onChange={(e) => { setSampleText(e.target.value); setCustomText(''); }}
                  className="w-full"
                >
                  <option value={SAMPLE_TEXTS.paragraph}>Paragraph</option>
                  <option value={SAMPLE_TEXTS.heading}>Heading</option>
                  <option value={SAMPLE_TEXTS.numbers}>Numbers & Currency</option>
                  <option value={SAMPLE_TEXTS.mixed}>SMS Report Data</option>
                  <option value={SAMPLE_TEXTS.code}>SQL Code</option>
                </select>
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Custom Text (optional)</label>
              <input
                type="text"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder="Type your own text to preview..."
                className="w-full"
              />
            </div>
          </div>

          {/* Live Preview */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-indigo-500" />
                <h2 className="text-sm font-semibold text-gray-900">Live Preview</h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded font-mono">
                  {selectedFont.name} · {selectedSize}px · {selectedWeight}
                </span>
                <button
                  onClick={() => handleCopy(`font-family: ${selectedFont.family}; font-size: ${selectedSize}px; font-weight: ${selectedWeight};`, 'css')}
                  className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                  title="Copy CSS"
                >
                  {copied === 'css' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div
              className="p-6 bg-gray-50 rounded-lg border border-gray-200 min-h-[120px]"
              style={{
                fontFamily: selectedFont.family,
                fontSize: `${selectedSize}px`,
                fontWeight: selectedWeight,
                lineHeight: 1.6,
              }}
            >
              {displayText}
            </div>

            {/* CSS snippet */}
            <div className="mt-4 p-3 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-400 font-mono">CSS</span>
                <button
                  onClick={() => handleCopy(`font-family: ${selectedFont.family};\nfont-size: ${selectedSize}px;\nfont-weight: ${selectedWeight};`, 'css2')}
                  className="text-xs text-gray-400 hover:text-white transition-colors"
                >
                  {copied === 'css2' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="text-sm text-green-400 font-mono whitespace-pre-wrap">
{`font-family: ${selectedFont.family};
font-size: ${selectedSize}px;
font-weight: ${selectedWeight};`}
              </pre>
            </div>
          </div>

          {/* Size Comparison Grid */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">Size Comparison — {selectedFont.name}</h2>
            <div className="space-y-3">
              {[10, 11, 12, 13, 14, 16, 18, 20, 24, 32].map(size => (
                <div key={size} className="flex items-baseline gap-4 py-2 border-b border-gray-50 last:border-0">
                  <span className="text-xs text-gray-400 font-mono w-12 flex-shrink-0 text-right">{size}px</span>
                  <span
                    style={{
                      fontFamily: selectedFont.family,
                      fontSize: `${size}px`,
                      fontWeight: selectedWeight,
                      lineHeight: 1.4,
                    }}
                    className="text-gray-900"
                  >
                    {selectedFont.name} — The quick brown fox
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Weight Comparison */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">Weight Comparison — {selectedFont.name} at {selectedSize}px</h2>
            <div className="space-y-3">
              {FONT_WEIGHTS.map(w => (
                <div key={w.value} className="flex items-baseline gap-4 py-2 border-b border-gray-50 last:border-0">
                  <span className="text-xs text-gray-400 font-mono w-24 flex-shrink-0 text-right">{w.label}</span>
                  <span
                    style={{
                      fontFamily: selectedFont.family,
                      fontSize: `${selectedSize}px`,
                      fontWeight: w.value,
                      lineHeight: 1.4,
                    }}
                    className="text-gray-900"
                  >
                    Way2Connect SMPP Platform — 12,450 SMS Delivered
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FontSamplePage;
